/**
 * POLLER — the lucky7 `Page.tsx` polling skeleton, lifted out and made reusable.
 *
 * Shape (unchanged from the shipped one, lucky7/Page.tsx :246-289): a setTimeout CHAIN, never
 * a setInterval. An interval fires whether or not the previous request came back, so a slow
 * network turns into a queue of overlapping requests that arrive out of order and hand the UI
 * a stale round after a fresh one. A chain cannot: the next delay is only armed once the
 * previous poll has finished.
 *
 * CADENCE (plan §5.3): 1200 ms idle, 400 ms within ±`nearMs` (4 s) of ANY round edge —
 * `locksAt`, `resolvesAt`, `settlesAt`, `nextOpensAt` — or while the caller says it is busy
 * (mid-animation). The point is that a round's INTERESTING moments are its edges: the lock,
 * the deal, the settle. Between them nothing changes and a poll is wasted budget, and the
 * budget is real — bg-player rate-limits a player to 300 `/state` calls a minute.
 *
 * PAUSE ON HIDDEN. A backgrounded tab keeps its timers (throttled) and keeps spending the
 * limiter. `document.hidden` stops the chain entirely and the `visibilitychange` back to
 * visible polls IMMEDIATELY — a returning player must see the truth, not the last frame from
 * three rounds ago.
 *
 * BACK OFF ON 429/5xx. A limiter hit or a server error answered at the same cadence is how a
 * client turns a blip into an outage. On any failure the delay doubles from 2 s to a 30 s
 * ceiling; the first success resets it. `ApiError.status` carries the code (429's body is not
 * the platform envelope, so its `code` is `http_error` — the STATUS is what to branch on).
 *
 * The poller owns no state of its own beyond the timer: what to fetch, what to keep and when
 * an edge is are all the caller's.
 */

import { ApiError } from '@baaligames/shared-ui/lib/api';

export type PollerOptions = {
  /** One poll. Throwing is how you say "that failed" — the poller backs off. */
  poll: () => Promise<void> | void;
  /** Absolute times to treat as edges, in the SAME domain as `now()`. Nullish entries are
   *  ignored, so a caller can pass a round's stamps before they exist. */
  edges?: () => Array<number | null | undefined>;
  /** The clock the edges are in. Default `performance.now()` — the domain the game Pages use. */
  now?: () => number;
  /** Relaxed cadence (ms). */
  idleMs?: number;
  /** Near-an-edge cadence (ms). */
  fastMs?: number;
  /** How close to an edge counts as near (ms), before AND after. */
  nearMs?: number;
  /** "Something is animating / a write is in flight" → poll fast regardless of edges. */
  busy?: () => boolean;
  /** Called for every failed poll (already backed off). Do not throw from it. */
  onError?: (e: unknown) => void;
  /** Set false to keep polling in a hidden tab (nothing here needs that today). */
  pauseWhenHidden?: boolean;
};

export type Poller = {
  /** Start the chain with an immediate poll. Idempotent. */
  start(): void;
  /** Stop the chain. Idempotent; safe inside a poll. */
  stop(): void;
  /** Poll NOW (after a bet ack, on tab focus). Never runs two polls at once. */
  kick(): void;
  running(): boolean;
  /** The delay the chain would arm right now (ms) — exposed for tests. */
  nextDelay(): number;
};

const BACKOFF_START_MS = 2_000;
const BACKOFF_MAX_MS = 30_000;

export function createPoller(o: PollerOptions): Poller {
  const idleMs = o.idleMs ?? 1200;
  const fastMs = o.fastMs ?? 400;
  const nearMs = o.nearMs ?? 4000;
  const now = o.now ?? (() => performance.now());
  const pauseWhenHidden = o.pauseWhenHidden !== false;

  let timer = 0;
  let running = false;
  let inFlight = false;
  let backoff = 0;                 // 0 = healthy
  let queuedKick = false;

  /** Near an edge, or busy → fast. */
  function nextDelay(): number {
    if (backoff) return backoff;
    try { if (o.busy?.()) return fastMs; } catch { /* a broken predicate must not stop polling */ }
    let edges: Array<number | null | undefined> = [];
    try { edges = o.edges?.() ?? []; } catch { edges = []; }
    const t = now();
    for (const e of edges) {
      if (e == null || !Number.isFinite(e)) continue;
      const d = (e as number) - t;
      if (d <= nearMs && d >= -nearMs) return fastMs;
    }
    return idleMs;
  }

  function arm(ms: number) {
    if (!running) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(run, Math.max(0, ms));
  }

  async function run() {
    if (!running || inFlight) return;
    if (pauseWhenHidden && typeof document !== 'undefined' && document.hidden) return; // visibilitychange restarts us
    inFlight = true;
    try {
      await o.poll();
      backoff = 0;
    } catch (e) {
      // 429 (limiter) and 5xx (server) are the two that MUST back off; a network throw is the
      // same class of problem from the client's side, so it is treated the same.
      const status = e instanceof ApiError ? e.status : 0;
      const retryable = status === 0 || status === 429 || status >= 500;
      backoff = retryable
        ? Math.min(backoff ? backoff * 2 : BACKOFF_START_MS, BACKOFF_MAX_MS)
        : backoff;                            // a 4xx that is not 429 is a bug, not congestion
      try { o.onError?.(e); } catch { /* never let a handler kill the chain */ }
    } finally {
      inFlight = false;
    }
    if (queuedKick) { queuedKick = false; arm(0); return; }
    arm(nextDelay());
  }

  const onVis = () => {
    if (!running) return;
    if (document.hidden) { window.clearTimeout(timer); return; }
    arm(0);                                    // back in front → the truth, immediately
  };

  return {
    start() {
      if (running) return;
      running = true;
      if (pauseWhenHidden && typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVis);
      }
      arm(0);
    },
    stop() {
      running = false;
      window.clearTimeout(timer);
      if (pauseWhenHidden && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
    },
    kick() {
      if (!running) return;
      if (inFlight) { queuedKick = true; return; }   // a poll is already reading the truth
      arm(0);
    },
    running: () => running,
    nextDelay,
  };
}

export default createPoller;
